import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";
import { GenericError } from "@via/components/error.tsx";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import Table, { TableSize } from "@via/components/table/table";
import TableContextMenu from "@via/components/table/table-context-menu";
import DeleteModal from "@via/components/delete-modal/delete-modal";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext";
import { EMPTY_TABLE_MESSAGE, ERROR, ROUTES } from "@via/utils/constants";
import { formatDateString } from "@via/utils/helpers";
import type { TableContextMenuInterface } from "@via/utils/interfaces";
import { useCallback, useMemo, useState, type JSX } from "react";
import CreateSchedulerModal from "@via/pages/runs/schedulers/create-scheduler-modal";
import SchedulerLogsModal from "@via/pages/runs/schedulers/scheduler-logs-modal";
import SchedulerSourcesModal from "@via/pages/runs/schedulers/scheduler-sources-modal";
import {
  useDeleteScheduler,
  useSchedulerList,
  useSchedulerSummary,
  useTriggerScheduler,
  useUpdateScheduler,
} from "@via/pages/runs/schedulers/schedulers.api";
import type { RunSchedulerSourceState, RunScheduler } from "common";

export default function RunSchedulers(): JSX.Element {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [schedulerToEdit, setSchedulerToEdit] = useState<RunScheduler | null>(
    null
  );
  const [logsScheduler, setLogsScheduler] = useState<{
    id: string | number;
    name: string;
    executionId?: number | null;
  } | null>(null);
  const [schedulerToDelete, setSchedulerToDelete] = useState<{
    id: string | number;
    name: string;
  } | null>(null);
  const [sourcesModal, setSourcesModal] = useState<{
    id: string | number;
    name: string;
    state: RunSchedulerSourceState;
  } | null>(null);

  const { data, isLoading, isError } = useSchedulerList();
  const { data: summaryResp } = useSchedulerSummary();
  const { mutate: updateScheduler, isPending: isUpdatePending } =
    useUpdateScheduler();
  const { mutate: triggerScheduler, isPending: isTriggerSchedulerPending } =
    useTriggerScheduler();
  const { mutateAsync: deleteScheduler, isPending: isDeletePending } =
    useDeleteScheduler();

  const schedulers: RunScheduler[] = useMemo(() => {
    if (!data) return [];
    return Array.isArray(data) ? data : (data.data ?? []);
  }, [data]);

  const summaryBySchedulerId = useMemo(() => {
    const list = summaryResp?.data ?? [];
    const map = new Map<number, (typeof list)[number]>();
    for (const item of list) {
      map.set(Number(item.schedulerId), item);
    }
    return map;
  }, [summaryResp]);

  const getContext = useCallback(() => {
    return {
      total: schedulers.length,
      schedulers: schedulers.map(s => ({
        id: s.id,
        name: s.name,
        projectId: s.projectId,
        enabled: s.enabled,
        sourceMethod: s.sourceMethod,
        watchPathSource: s.watchPathSource,
        watchPathTarget: s.watchPathTarget,
        sampleTrackerId: s.sampleTrackerId,
        sampleTrackerCollectionId: s.sampleTrackerCollectionId,
        watchPathField: s.watchPathField,
        statusField: s.statusField,
        templateRunId: s.templateRunId,
        intervalMinutes: s.intervalMinutes,
      })),
    };
  }, [schedulers]);

  useRegisterCortexContext({
    routePattern: ROUTES.RUN_SCHEDULERS,
    contextName: "Run Schedulers",
    getContext,
    systemPrompt:
      "You are analyzing run schedulers. Help answer questions about configured schedulers and how they trigger runs.",
    enabled: true,
  });

  const columnHelper = createColumnHelper<RunScheduler>();
  const columns = [
    columnHelper.accessor("name", {
      header: () => <span>Name</span>,
      cell: ({ row }) => <span className="truncate">{row.original.name}</span>,
    }),
    columnHelper.accessor("sourceMethod", {
      header: () => <span>Source</span>,
      cell: info => (
        <span className="truncate">
          {info.getValue() === "sample-tracker"
            ? "Sample Tracker"
            : "Watch Path"}
        </span>
      ),
    }),
    columnHelper.accessor("intervalMinutes", {
      header: () => <span>Interval</span>,
      cell: info => <span className="truncate">{info.getValue()} min</span>,
      enableSorting: false,
    }),
    columnHelper.display({
      id: "counts_submitted",
      header: () => <span>Submitted</span>,
      cell: ({ row }) => {
        const item = summaryBySchedulerId.get(Number(row.original.id));
        const v = item?.current?.submitted;
        return (
          <button
            type="button"
            onClick={() =>
              setSourcesModal({
                id: row.original.id,
                name: row.original.name,
                state: "submitted",
              })
            }
            className="truncate text-indigo-600 hover:text-indigo-500">
            {v ?? "-"}
          </button>
        );
      },
      enableSorting: false,
    }),
    columnHelper.display({
      id: "counts_pending",
      header: () => <span>Pending</span>,
      cell: ({ row }) => {
        const item = summaryBySchedulerId.get(Number(row.original.id));
        const v = item?.current?.pending;
        return (
          <button
            type="button"
            onClick={() =>
              setSourcesModal({
                id: row.original.id,
                name: row.original.name,
                state: "pending",
              })
            }
            className="truncate text-indigo-600 hover:text-indigo-500">
            {v ?? "-"}
          </button>
        );
      },
      enableSorting: false,
    }),
    columnHelper.display({
      id: "counts_errors",
      header: () => <span>Errors</span>,
      cell: ({ row }) => {
        const item = summaryBySchedulerId.get(Number(row.original.id));
        const v = item?.current?.errors;
        return (
          <button
            type="button"
            onClick={() =>
              setSourcesModal({
                id: row.original.id,
                name: row.original.name,
                state: "error",
              })
            }
            className="truncate text-indigo-600 hover:text-indigo-500">
            {v ?? "-"}
          </button>
        );
      },
      enableSorting: false,
    }),
    columnHelper.accessor("enabled", {
      header: () => <span>Enabled</span>,
      cell: ({ row }) => {
        const enabled = row.original.enabled;
        return (
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={enabled}
              disabled={isUpdatePending}
              onChange={e => {
                updateScheduler({
                  id: row.original.id,
                  data: { enabled: e.target.checked },
                });
              }}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
            />
            <span className="text-sm text-gray-700">
              {enabled ? "On" : "Off"}
            </span>
          </label>
        );
      },
      enableSorting: false,
    }),
    columnHelper.accessor("updatedAt", {
      header: () => <span>Updated</span>,
      cell: ({ row }) =>
        row.original.updatedAt ? (
          <span className="truncate">
            {formatDateString(row.original.updatedAt, "LLL")}
          </span>
        ) : (
          <span className="text-gray-400">-</span>
        ),
    }),
    columnHelper.display({
      id: "actions",
      header: () => <span>Actions</span>,
      cell: ({ row }) => {
        const options: TableContextMenuInterface[] = [
          {
            name: "Manually Trigger Now",
            href: "#",
            disabled: isTriggerSchedulerPending,
            loading: isTriggerSchedulerPending,
          },
          { name: "View Scheduler Logs", href: "#" },
          { name: "View Status Details", href: "#" },
          { name: "Edit", href: "#" },
          { name: "Delete", href: "#" },
        ];
        return (
          <div className="flex items-center justify-start">
            <TableContextMenu
              options={options}
              onSelect={option => {
                if (option.name === "Manually Trigger Now") {
                  triggerScheduler(
                    { id: row.original.id },
                    {
                      onSuccess: resp => {
                        setLogsScheduler({
                          id: row.original.id,
                          name: row.original.name,
                          executionId: resp.executionId,
                        });
                      },
                    }
                  );
                }
                if (option.name === "View Scheduler Logs") {
                  setLogsScheduler({
                    id: row.original.id,
                    name: row.original.name,
                  });
                }
                if (option.name === "View Status Details") {
                  setSourcesModal({
                    id: row.original.id,
                    name: row.original.name,
                    state: "submitted",
                  });
                }
                if (option.name === "Edit") {
                  setSchedulerToEdit(row.original);
                }
                if (option.name === "Delete") {
                  setSchedulerToDelete({
                    id: row.original.id,
                    name: row.original.name,
                  });
                }
              }}
            />
          </div>
        );
      },
    }),
  ] as Array<ColumnDef<RunScheduler>>;

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <p className="mt-4 text-sm text-gray-600">
            Create and manage automated run submissions.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setIsCreateModalOpen(true)}
          className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500">
          Create Scheduler
        </button>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-3">
          <SkeletonLoader count={12} />
        </div>
      ) : isError ? (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      ) : (
        <Table
          data={schedulers}
          columns={columns}
          emptyMessage={EMPTY_TABLE_MESSAGE.RUN_SCHEDULERS}
          size={TableSize.Big}
        />
      )}

      <CreateSchedulerModal
        open={isCreateModalOpen}
        setOpen={setIsCreateModalOpen}
      />

      <CreateSchedulerModal
        open={!!schedulerToEdit}
        setOpen={open => {
          if (!open) setSchedulerToEdit(null);
        }}
        mode="edit"
        initialScheduler={schedulerToEdit}
      />

      {schedulerToDelete && (
        <DeleteModal
          item={schedulerToDelete}
          setItem={setSchedulerToDelete}
          title="Delete Scheduler"
          isLoading={isDeletePending}
          deleteFunction={async () => {
            await deleteScheduler({ id: schedulerToDelete.id });
          }}
        />
      )}

      <SchedulerLogsModal
        open={!!logsScheduler}
        setOpen={open => {
          if (!open) setLogsScheduler(null);
        }}
        schedulerId={logsScheduler?.id ?? null}
        schedulerName={logsScheduler?.name}
        initialExecutionId={logsScheduler?.executionId ?? null}
      />

      <SchedulerSourcesModal
        open={!!sourcesModal}
        setOpen={open => {
          if (!open) setSourcesModal(null);
        }}
        schedulerId={sourcesModal?.id ?? null}
        schedulerName={sourcesModal?.name}
        initialState={sourcesModal?.state ?? "submitted"}
      />
    </>
  );
}
